import { useState } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { useMutation } from '@tanstack/react-query';
import { useCanonical } from '../hooks/useCanonical';
import { useAuthStore } from '../store/authStore';
import { authApi } from '../services/api';
import Card from '../components/common/Card';
import Button from '../components/common/Button';
import Input from '../components/common/Input';
import Alert from '../components/common/Alert';
import { User, Lock, Building } from 'lucide-react';

const profileSchema = z.object({
  name: z.string().min(2, 'Name must be at least 2 characters'),
  email: z.string().email('Invalid email address'),
});

const passwordSchema = z
  .object({
    currentPassword: z.string().min(6, 'Password must be at least 6 characters'),
    newPassword: z.string().min(6, 'Password must be at least 6 characters'),
    confirmPassword: z.string(),
  })
  .refine((data) => data.newPassword === data.confirmPassword, {
    message: "Passwords don't match",
    path: ['confirmPassword'],
  });

type ProfileFormData = z.infer<typeof profileSchema>;
type PasswordFormData = z.infer<typeof passwordSchema>;

export default function ProfilePage() {
  useCanonical('/profile');
  const { user, setAuth } = useAuthStore();
  const [profileSuccess, setProfileSuccess] = useState('');
  const [profileError, setProfileError] = useState('');
  const [passwordSuccess, setPasswordSuccess] = useState('');
  const [passwordError, setPasswordError] = useState('');

  const {
    register: registerProfile,
    handleSubmit: handleProfileSubmit,
    formState: { errors: profileErrors },
  } = useForm<ProfileFormData>({
    resolver: zodResolver(profileSchema),
    defaultValues: {
      name: user?.name || '',
      email: user?.email || '',
    },
  });

  const {
    register: registerPassword,
    handleSubmit: handlePasswordSubmit,
    formState: { errors: passwordErrors },
    reset: resetPassword,
  } = useForm<PasswordFormData>({
    resolver: zodResolver(passwordSchema),
  });

  const profileMutation = useMutation({
    mutationFn: (data: ProfileFormData) => authApi.updateProfile(data),
    onSuccess: async (response) => {
      setProfileSuccess('Profile updated successfully');
      setProfileError('');
      
      // Fetch fresh user data to get updated organization name
      try {
        const userResponse = await authApi.getCurrentUser();
        if (userResponse.success && userResponse.data.user) {
          setAuth(
            {
              id: userResponse.data.user.id,
              email: userResponse.data.user.email,
              name: userResponse.data.user.full_name,
              role: userResponse.data.user.role,
              organization: userResponse.data.user.organization_name || userResponse.data.user.organization_id,
            },
            useAuthStore.getState().token!
          );
        }
      } catch (error) {
        console.error('Failed to refresh user data:', error);
        // Fallback to response data
        if (user) {
          setAuth(
            {
              ...user,
              ...response.data.user,
            },
            useAuthStore.getState().token!
          );
        }
      }
    },
    onError: (err: any) => {
      setProfileError(
        err.response?.data?.message || 'Failed to update profile'
      );
      setProfileSuccess('');
    },
  });

  const passwordMutation = useMutation({
    mutationFn: (data: { currentPassword: string; newPassword: string }) =>
      authApi.changePassword(data),
    onSuccess: () => {
      setPasswordSuccess('Password changed successfully');
      setPasswordError('');
      resetPassword();
    },
    onError: (err: any) => {
      setPasswordError(
        err.response?.data?.message || 'Failed to change password'
      );
      setPasswordSuccess('');
    },
  });

  const onProfileSubmit = (data: ProfileFormData) => {
    profileMutation.mutate(data);
  };

  const onPasswordSubmit = (data: PasswordFormData) => {
    const { confirmPassword, ...passwordData } = data;
    passwordMutation.mutate(passwordData);
  };

  return (
    <div className="max-w-4xl mx-auto space-y-6">
      {/* Header */}
      <div>
        <h1 className="text-3xl font-display font-bold text-gray-900 dark:text-white">
          Profile
        </h1>
        <p className="text-gray-600 dark:text-gray-300 mt-1">
          Manage your profile information and account settings
        </p>
      </div>

      {/* Profile Settings */}
      <Card>
        <div className="flex items-center gap-2 mb-6">
          <User className="w-6 h-6 text-primary-600" />
          <h2 className="text-xl font-semibold text-gray-900 dark:text-white">
            Profile Information
          </h2>
        </div>

        {profileSuccess && (
          <Alert type="success" className="mb-4">
            {profileSuccess}
          </Alert>
        )}

        {profileError && (
          <Alert type="error" className="mb-4">
            {profileError}
          </Alert>
        )}

        <form onSubmit={handleProfileSubmit(onProfileSubmit)} className="space-y-4">
          <Input
            label="Full Name"
            error={profileErrors.name?.message}
            {...registerProfile('name')}
          />

          <Input
            label="Email"
            type="email"
            error={profileErrors.email?.message}
            {...registerProfile('email')}
          />

          <Input
            label="Organization"
            value={user?.organization || ''}
            disabled
            readOnly
            className="bg-gray-50 cursor-not-allowed"
          />

          <div className="flex justify-end pt-4 border-t">
            <Button type="submit" loading={profileMutation.isPending}>
              Save Changes
            </Button>
          </div>
        </form>
      </Card>

      {/* Password Settings */}
      <Card>
        <div className="flex items-center gap-2 mb-6">
          <Lock className="w-6 h-6 text-primary-600" />
          <h2 className="text-xl font-semibold text-gray-900 dark:text-white">
            Change Password
          </h2>
        </div>

        {passwordSuccess && (
          <Alert type="success" className="mb-4">
            {passwordSuccess}
          </Alert>
        )}

        {passwordError && (
          <Alert type="error" className="mb-4">
            {passwordError}
          </Alert>
        )}

        <form onSubmit={handlePasswordSubmit(onPasswordSubmit)} className="space-y-4">
          <Input
            label="Current Password"
            type="password"
            error={passwordErrors.currentPassword?.message}
            {...registerPassword('currentPassword')}
          />

          <Input
            label="New Password"
            type="password"
            error={passwordErrors.newPassword?.message}
            {...registerPassword('newPassword')}
          />

          <Input
            label="Confirm New Password"
            type="password"
            error={passwordErrors.confirmPassword?.message}
            {...registerPassword('confirmPassword')}
          />

          <div className="flex justify-end pt-4 border-t">
            <Button type="submit" loading={passwordMutation.isPending}>
              Change Password
            </Button>
          </div>
        </form>
      </Card>

      {/* Account Info */}
      <Card className="bg-gray-50">
        <div className="flex items-center gap-2 mb-4">
          <Building className="w-6 h-6 text-gray-600 dark:text-gray-400" />
          <h2 className="text-xl font-semibold text-gray-900 dark:text-white">
            Account Information
          </h2>
        </div>
        <dl className="space-y-3 text-sm">
          <div>
            <dt className="text-gray-600 dark:text-gray-400">Account Type</dt>
            <dd className="font-medium text-gray-900 dark:text-white mt-1">
              {user?.role || 'User'}
            </dd>
          </div>
          <div>
            <dt className="text-gray-600 dark:text-gray-400">Member Since</dt>
            <dd className="font-medium text-gray-900 dark:text-white mt-1">
              {(user as any)?.created_at
                ? new Date((user as any).created_at).toLocaleDateString()
                : 'N/A'}
            </dd>
          </div>
        </dl>
      </Card>
    </div>
  );
}

// Made with Bob
